import React, {FC} from 'react';
import {connect} from 'react-redux';

import {ResolveStateProps} from '../../../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {createSelector, makeMapStateToProps} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {DetailedActivityConversationLinkedModel} from '../../../../../models/api/activityModel';
import {ActivitySourceVisualTypesEnum} from '../../../../../models/api/activitySourceModel';
import {AppState} from '../../../../../store/appState';
import {getConversations} from '../../../../../store/data/dataSelectors';
import {getFindTeammate} from '../../../../../store/data/teammates/teammatesSelectors';
import {LinkedConversationViewer} from './linkedConversationsViewer';

/*
 * Props.
 */

interface LinkedConversationsViewerContainerProps {
  panelId: ViewerPanelId;
  activity: DetailedActivityConversationLinkedModel;
  isFirstOfKind: boolean;
}

type LinkedConversationsViewerStateProps = ResolveStateProps<typeof mapStateToProps>;
type LinkedConversationsViewerContainerComponentProps = LinkedConversationsViewerContainerProps &
  LinkedConversationsViewerStateProps;

/*
 * Component.
 */

const LinkedConversationsViewerContainerComponent: FC<LinkedConversationsViewerContainerComponentProps> = (
  props,
) => {
  const {panelId, activity, isFirstOfKind, currentConversation, linkedConversation, sourceTeammate} = props;

  if (!linkedConversation) {
    return null;
  }

  return (
    <LinkedConversationViewer
      panelId={panelId}
      activity={activity}
      isFirstOfKind={isFirstOfKind}
      currentConversation={currentConversation}
      linkedConversation={linkedConversation}
      sourceTeammate={sourceTeammate}
    />
  );
};

/*
 * Selectors.
 */

function getActivity(_state: AppState, props: LinkedConversationsViewerContainerProps) {
  return props.activity;
}

function makeGetSourceTeammate() {
  return createSelector([getActivity, getFindTeammate], (activity, findTeammate) => {
    const {source} = activity;
    if (source.visualType !== ActivitySourceVisualTypesEnum.TEAMMATE) {
      return undefined;
    }

    return findTeammate(source.teammateId);
  });
}

function makeGetCurrentConversation() {
  return createSelector(
    [getActivity, getConversations],
    (activity, conversations) => conversations[activity.conversationId],
  );
}

function makeGetLinkedConversation() {
  return createSelector([getActivity, getConversations], (activity, conversations) =>
    activity.linkedConversationId ? conversations[activity.linkedConversationId] : undefined,
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<LinkedConversationsViewerContainerProps>()(() => ({
  currentConversation: makeGetCurrentConversation(),
  linkedConversation: makeGetLinkedConversation(),
  sourceTeammate: makeGetSourceTeammate(),
}));

export const LinkedConversationsViewerContainer = connect(mapStateToProps)(
  LinkedConversationsViewerContainerComponent,
);
